import { useCallback, useEffect, useState } from "react";
import { Layers, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const MAPPING_TYPES = ["EXACT", "PARTIAL", "RELATED"];
const MAPPING_TYPE_LABELS = { EXACT: "Allineato (EXACT)", PARTIAL: "Parziale (PARTIAL)", RELATED: "Correlato (RELATED)" };
const ALIGNMENT_LABELS = {
  ALLINEATO: "Allineato", PARZIALE: "Parzialmente allineato",
  SPECIFICO_PMI_BANCHE: "Specifico PMI-Banche (N.A. in VSME)",
};
const ALIGNMENT_STYLE = {
  ALLINEATO: "bg-green-50 text-green-800 border-green-300",
  PARZIALE: "bg-sky-50 text-sky-800 border-sky-300",
  SPECIFICO_PMI_BANCHE: "bg-amber-50 text-amber-800 border-amber-300",
};

export default function AdminFrameworks() {
  const [frameworks, setFrameworks] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [f, c] = await Promise.all([
        api.get("/admin/frameworks"),
        api.get("/admin/frameworks/coverage"),
      ]);
      setFrameworks(f.data.frameworks);
      setCoverage(c.data);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateMapping = async (mapping, updates) => {
    setSavingId(mapping.id);
    try {
      await api.put(`/admin/frameworks/mappings/${mapping.id}`, updates);
      toast.success("Mapping aggiornato", {
        description: `${mapping.mef_code} → ${mapping.vsme_code}`,
      });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    } finally {
      setSavingId(null);
    }
  };

  if (error) {
    return <p className="text-sm text-red-700" data-testid="admin-frameworks-error">{error}</p>;
  }
  if (!frameworks) {
    return <p className="text-sm text-slate-600" data-testid="admin-frameworks-loading">Caricamento framework…</p>;
  }

  return (
    <div className="space-y-6" data-testid="admin-frameworks-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Layers className="w-7 h-7 text-green-800" aria-hidden="true" />
          Framework Management
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Framework di rendicontazione secondari e matrice di interoperabilità con il VSME.
        </p>
      </div>

      {frameworks.map((f) => (
        <Card key={f.id} className="border-slate-200" data-testid={`framework-card-${f.framework_code}`}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base font-semibold text-slate-900">{f.framework_name}</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {f.version_name} · {f.datapoints_count} informazioni · Fonte: {f.source_reference}
                </p>
              </div>
              <Badge variant="outline" className="bg-green-50 text-green-800 border-green-300 font-semibold">
                {f.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-600 mb-4 leading-relaxed">{f.notes}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`mapping-table-${f.framework_code}`}>
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">MEF</th>
                    <th className="py-2 pr-3 font-semibold">Informazione</th>
                    <th className="py-2 pr-3 font-semibold">Datapoint VSME</th>
                    <th className="py-2 pr-3 font-semibold">Rif. ufficiale</th>
                    <th className="py-2 pr-3 font-semibold">Tipo mapping</th>
                    <th className="py-2 pr-3 font-semibold">Qualità</th>
                    <th className="py-2 font-semibold">Attivo</th>
                  </tr>
                </thead>
                <tbody>
                  {f.mappings.map((m) => (
                    <tr key={m.id} className={`border-b border-slate-100 last:border-0 ${m.active ? "" : "opacity-50"}`}
                      data-testid={`mapping-row-${m.mef_code}-${m.vsme_code}`}>
                      <td className="py-2 pr-3 font-mono text-xs text-slate-700 whitespace-nowrap">{m.mef_code}</td>
                      <td className="py-2 pr-3 text-xs text-slate-600 max-w-[280px]">{m.mef_title}</td>
                      <td className="py-2 pr-3 font-mono text-xs text-slate-700 whitespace-nowrap">
                        {m.vsme_code}
                        {m.notes && <span className="block text-[10px] text-amber-700 font-sans">{m.notes}</span>}
                      </td>
                      <td className="py-2 pr-3 text-[11px] text-slate-500 whitespace-nowrap"
                        data-testid={`mapping-ref-${m.mef_code}-${m.vsme_code}`}>{m.vsme_ref || "—"}</td>
                      <td className="py-2 pr-3">
                        <Select
                          value={m.mapping_type}
                          disabled={savingId === m.id}
                          onValueChange={(v) => updateMapping(m, { mapping_type: v })}
                        >
                          <SelectTrigger className="h-8 w-[190px] text-xs"
                            data-testid={`mapping-type-${m.mef_code}-${m.vsme_code}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MAPPING_TYPES.map((t) => (
                              <SelectItem key={t} value={t} data-testid={`mapping-type-option-${t}`}>
                                {MAPPING_TYPE_LABELS[t]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={m.quality === "OFFICIAL"
                            ? "bg-green-100 text-green-800 border-green-300 text-[10px]"
                            : "bg-amber-50 text-amber-800 border-amber-300 text-[10px]"}
                            data-testid={`mapping-quality-${m.mef_code}-${m.vsme_code}`}>
                            {m.quality === "OFFICIAL" ? "OFFICIAL" : "VERIFIED INTERNAL"}
                          </Badge>
                          {m.quality !== "OFFICIAL" && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-green-700"
                              disabled={savingId === m.id}
                              onClick={() => updateMapping(m, { quality: "OFFICIAL" })}
                              data-testid={`mapping-promote-${m.mef_code}-${m.vsme_code}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                              Promuovi
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-2">
                        <Switch
                          checked={m.active}
                          disabled={savingId === m.id}
                          onCheckedChange={(v) => updateMapping(m, { active: v })}
                          data-testid={`mapping-active-${m.mef_code}-${m.vsme_code}`}
                          aria-label={`Attiva mapping ${m.mef_code} ${m.vsme_code}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {coverage && (
        <Card className="border-slate-200" data-testid="coverage-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-900">
              Copertura ufficiale — Tabella di interoperabilità MEF↔VSME (dic. 2025)
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1" data-testid="coverage-stats">
              {coverage.stats.total} indicatori · {coverage.stats.aligned} allineati ·{" "}
              {coverage.stats.partial} parziali · {coverage.stats.pmi_specific} specifici PMI-Banche ·{" "}
              {coverage.stats.with_mapping} con mapping attivo ·{" "}
              {coverage.stats.with_question} con domanda dedicata
            </p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="coverage-table">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">MEF</th>
                    <th className="py-2 pr-3 font-semibold">Informazione</th>
                    <th className="py-2 pr-3 font-semibold">Allineamento ufficiale</th>
                    <th className="py-2 pr-3 font-semibold">Rif. VSME</th>
                    <th className="py-2 font-semibold">Dato canonico</th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.items.map((i) => (
                    <tr key={i.mef_code} className="border-b border-slate-100 last:border-0"
                      data-testid={`coverage-row-${i.mef_code}`}>
                      <td className="py-2 pr-3 font-mono text-xs text-slate-700 whitespace-nowrap">{i.mef_code}</td>
                      <td className="py-2 pr-3 text-xs text-slate-600 max-w-[260px]">{i.title}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline"
                          className={`text-[10px] ${ALIGNMENT_STYLE[i.official_alignment] || ""}`}
                          data-testid={`coverage-alignment-${i.mef_code}`}>
                          {ALIGNMENT_LABELS[i.official_alignment] || i.official_alignment}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-[11px] text-slate-500 whitespace-nowrap">{i.vsme_ref || "—"}</td>
                      <td className="py-2 text-[11px] text-slate-600">
                        {i.mapped_vsme.length > 0
                          ? <span className="font-mono">{i.mapped_vsme.join(", ")}</span>
                          : i.question_code
                            ? <span>Domanda dedicata <span className="font-mono">{i.question_code}</span></span>
                            : <span className="text-red-700">Non coperto</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
